"use client";

import OptionUnstyled, {
  optionUnstyledClasses,
} from "@mui/base/OptionUnstyled";
import PopperUnstyled from "@mui/base/PopperUnstyled";
import SelectUnstyled, {
  selectUnstyledClasses,
} from "@mui/base/SelectUnstyled";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import Autocomplete from "@mui/material/Autocomplete";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/system";
import ChevronDown from "public/images/icons/chevron-down.svg";
import { useEffect } from "react";
import * as React from "react";
import NumberFormat from "react-number-format";
import OtpInput from "react18-input-otp";
import { displayFullAddress, filterAddresses } from "utils/helper";
import { useDebounce } from "utils/hooks";
import { getPrimaryColor, getPrimaryFont } from "utils/themeConfig";

import styles from "@/styles/onboarding.module.scss";
import "../../theme-css/dropdown-theme.scss";

const grey = {
  100: "#E7EBF0",
  200: "#E0E3E7",
  300: "#CDD2D7",
  400: "#B2BAC2",
  500: "#A0AAB4",
  600: "#6F7E8C",
  700: "#3E5060",
  800: "#2D3843",
  900: "#1A2027",
};

const onmoPrimary = getPrimaryColor()?.slice(1);
const fontPrimary = getPrimaryFont();

const camelize = str => {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/g, "");
};

const linkIcon = (
  <span>
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.6667 10.6667H1.33333V1.33333H6V0H1.33333C0.979711 0 0.640573 0.140476 0.390524 0.390524C0.140476 0.640573 0 0.979711 0 1.33333V10.6667C0 11.0203 0.140476 11.3594 0.390524 11.6095C0.640573 11.8595 0.979711 12 1.33333 12H10.6667C11.4 12 12 11.4 12 10.6667V6H10.6667V10.6667ZM7.33333 0V1.33333H9.72667L3.17333 7.88667L4.11333 8.82667L10.6667 2.27333V4.66667H12V0H7.33333Z"
        fill="#008675"
      />
    </svg>
  </span>
);

export const ListIcon = ({
  text = "",
  center = false,
  link = false,
  large = false,
  align = false,
}) => (
  <p
    className={`font-medium text-${large ? "base" : "xs"} flex gap-2.5 ${
      align ? "items-center" : "items-start"
    } ${center ? "justify-center" : null}`}
  >
    <span>
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="9"
          cy="9"
          r="7.23529"
          fill="#008675"
          stroke="#008675"
          strokeWidth="3.52941"
        />
        <rect x="7" y="4" width="4" height="10" fill="white" />
      </svg>
    </span>
    <span>
      <span>{text}</span>
      <span className="inline-flex">
        {" "}
        {link ? (
          <a className="ml-[5px]" href={link}>
            {linkIcon}
          </a>
        ) : null}
      </span>
    </span>
  </p>
);

export const errorIcon = (
  <span>
    <svg
      width="19"
      height="18"
      viewBox="0 0 19 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="9.5"
        cy="9"
        r="7.23529"
        transform="rotate(-90 9.5 9)"
        fill="#FC0235"
        stroke="#FC0235"
        strokeWidth="3.52941"
      />
      <rect
        x="4.5"
        y="11"
        width="4"
        height="10"
        transform="rotate(-90 4.5 11)"
        fill="white"
      />
    </svg>
  </span>
);
const StyledButton = styled("button")(
  ({ theme }) => `
    position: relative;
    font-size: 16px;
    width: 100%;
    background-color: #fff !important;
    border: 1px solid #C4C4C4;
    border-radius: 5px;
    padding: 0 10px;
    height: 40px;
    text-align: left;

    &:hover {
      border-color: #63666A;
    }
  
    &.${selectUnstyledClasses.expanded} {
      &::after {
        content: url("data:image/svg+xml, %3Csvg width='23' height='12' viewBox='0 0 23 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M21.1166 11.4283C20.936 11.4285 20.7572 11.393 20.5904 11.3238C20.4236 11.2546 20.2722 11.153 20.1449 11.0249L13.1113 3.99228C12.8985 3.77941 12.6458 3.61055 12.3678 3.49535C12.0897 3.38014 11.7916 3.32085 11.4906 3.32085C11.1897 3.32085 10.8916 3.38014 10.6135 3.49535C10.3354 3.61055 10.0828 3.77941 9.86998 3.99228L2.8474 11.0158C2.72056 11.1471 2.56883 11.2519 2.40108 11.3239C2.23332 11.396 2.05289 11.4339 1.87032 11.4355C1.68775 11.4371 1.50669 11.4023 1.33771 11.3332C1.16873 11.264 1.01521 11.1619 0.886105 11.0328C0.757002 10.9037 0.654903 10.7502 0.585768 10.5812C0.51663 10.4122 0.48184 10.2312 0.483427 10.0486C0.485014 9.86603 0.522945 9.6856 0.595007 9.51784C0.66707 9.35009 0.771818 9.19837 0.903145 9.07153L7.92573 2.04803C8.87181 1.1037 10.1539 0.573332 11.4906 0.573332C12.8274 0.573332 14.1095 1.1037 15.0556 2.04803L22.0891 9.08069C22.2816 9.27299 22.4126 9.51805 22.4657 9.78486C22.5188 10.0517 22.4916 10.3282 22.3875 10.5795C22.2834 10.8309 22.107 11.0457 21.8808 11.1967C21.6546 11.3478 21.3886 11.4284 21.1166 11.4283Z' fill='%23${onmoPrimary}'/%3E%3C/svg%3E%0A");
      }
    }
  
    &::after {
      content: url("data:image/svg+xml,%3Csvg width='23' height='12' viewBox='0 0 23 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1.88344 0.571724C2.064 0.571482 2.24283 0.606998 2.4096 0.676224C2.57637 0.74545 2.72779 0.847014 2.8551 0.975058L9.88869 8.00772C10.1015 8.22059 10.3542 8.38945 10.6322 8.50465C10.9103 8.61986 11.2084 8.67915 11.5094 8.67915C11.8103 8.67915 12.1084 8.61986 12.3865 8.50465C12.6646 8.38945 12.9172 8.22059 13.13 8.00772L20.1526 0.984224C20.2794 0.852897 20.4312 0.748147 20.5989 0.676085C20.7667 0.604022 20.9471 0.566092 21.1297 0.564505C21.3122 0.562919 21.4933 0.597708 21.6623 0.666844C21.8313 0.735981 21.9848 0.838079 22.1139 0.967181C22.243 1.09628 22.3451 1.2498 22.4142 1.41879C22.4834 1.58777 22.5182 1.76883 22.5166 1.9514C22.515 2.13397 22.4771 2.3144 22.405 2.48216C22.3329 2.64991 22.2282 2.80163 22.0969 2.92847L15.0743 9.95197C14.1282 10.8963 12.8461 11.4267 11.5094 11.4267C10.1726 11.4267 8.89052 10.8963 7.94444 9.95197L0.910853 2.91931C0.71843 2.72701 0.587374 2.48195 0.534267 2.21514C0.481161 1.94834 0.508392 1.67178 0.612514 1.42045C0.716636 1.16912 0.892969 0.954333 1.1192 0.803255C1.34543 0.652176 1.6114 0.571601 1.88344 0.571724Z' fill='%23${onmoPrimary}'/%3E%3C/svg%3E%0A ");
      float: right;
    }
    `,
);

const StyledButtonMultiLine = styled("button")(
  ({ theme }) => `
    position: relative;
    font-size: 16px;
    width: 100%;
    background-color: #fff !important;
    border: 1px solid #C4C4C4;
    border-radius: 5px;
    padding: 0 10px;
    @media (max-width: 460px) {
      height: 55px;
    }
    height: 40px;
    text-align: left;

    &:hover {
      border-color: #63666A;
    }
  
    &.${selectUnstyledClasses.expanded} {
      &::after {
        content: url("data:image/svg+xml, %3Csvg width='23' height='12' viewBox='0 0 23 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M21.1166 11.4283C20.936 11.4285 20.7572 11.393 20.5904 11.3238C20.4236 11.2546 20.2722 11.153 20.1449 11.0249L13.1113 3.99228C12.8985 3.77941 12.6458 3.61055 12.3678 3.49535C12.0897 3.38014 11.7916 3.32085 11.4906 3.32085C11.1897 3.32085 10.8916 3.38014 10.6135 3.49535C10.3354 3.61055 10.0828 3.77941 9.86998 3.99228L2.8474 11.0158C2.72056 11.1471 2.56883 11.2519 2.40108 11.3239C2.23332 11.396 2.05289 11.4339 1.87032 11.4355C1.68775 11.4371 1.50669 11.4023 1.33771 11.3332C1.16873 11.264 1.01521 11.1619 0.886105 11.0328C0.757002 10.9037 0.654903 10.7502 0.585768 10.5812C0.51663 10.4122 0.48184 10.2312 0.483427 10.0486C0.485014 9.86603 0.522945 9.6856 0.595007 9.51784C0.66707 9.35009 0.771818 9.19837 0.903145 9.07153L7.92573 2.04803C8.87181 1.1037 10.1539 0.573332 11.4906 0.573332C12.8274 0.573332 14.1095 1.1037 15.0556 2.04803L22.0891 9.08069C22.2816 9.27299 22.4126 9.51805 22.4657 9.78486C22.5188 10.0517 22.4916 10.3282 22.3875 10.5795C22.2834 10.8309 22.107 11.0457 21.8808 11.1967C21.6546 11.3478 21.3886 11.4284 21.1166 11.4283Z' fill='%23${onmoPrimary}'/%3E%3C/svg%3E%0A");
      }
    }
  
    &::after {
      content: url("data:image/svg+xml,%3Csvg width='23' height='12' viewBox='0 0 23 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1.88344 0.571724C2.064 0.571482 2.24283 0.606998 2.4096 0.676224C2.57637 0.74545 2.72779 0.847014 2.8551 0.975058L9.88869 8.00772C10.1015 8.22059 10.3542 8.38945 10.6322 8.50465C10.9103 8.61986 11.2084 8.67915 11.5094 8.67915C11.8103 8.67915 12.1084 8.61986 12.3865 8.50465C12.6646 8.38945 12.9172 8.22059 13.13 8.00772L20.1526 0.984224C20.2794 0.852897 20.4312 0.748147 20.5989 0.676085C20.7667 0.604022 20.9471 0.566092 21.1297 0.564505C21.3122 0.562919 21.4933 0.597708 21.6623 0.666844C21.8313 0.735981 21.9848 0.838079 22.1139 0.967181C22.243 1.09628 22.3451 1.2498 22.4142 1.41879C22.4834 1.58777 22.5182 1.76883 22.5166 1.9514C22.515 2.13397 22.4771 2.3144 22.405 2.48216C22.3329 2.64991 22.2282 2.80163 22.0969 2.92847L15.0743 9.95197C14.1282 10.8963 12.8461 11.4267 11.5094 11.4267C10.1726 11.4267 8.89052 10.8963 7.94444 9.95197L0.910853 2.91931C0.71843 2.72701 0.587374 2.48195 0.534267 2.21514C0.481161 1.94834 0.508392 1.67178 0.612514 1.42045C0.716636 1.16912 0.892969 0.954333 1.1192 0.803255C1.34543 0.652176 1.6114 0.571601 1.88344 0.571724Z' fill='%23${onmoPrimary}'/%3E%3C/svg%3E%0A ");
      float: right;
    }
    `,
);

const StyledListbox = styled("ul")(
  ({ theme }) => `
    font-family: ${fontPrimary};
    color: #232323;
    font-weight: 400;
    font-size: 0.875rem;
    box-sizing: border-box;
    padding: 5px;
    margin: 10px 0;
    min-width: 320px;
    background: #fff;
    border: 1px solid #C4C4C4;
    border-radius: 0.75em;
    overflow: auto;
    outline: 0px;
    &:hover {
        border: 1px solid #63666A;
    }
    `,
);

export const StyledOption = styled(OptionUnstyled)(
  ({ theme }) => `
    list-style: none;
    padding: 8px;
    border-radius: 5px;
    cursor: default;
    font-size: '16px',
    &:last-of-type {
      border-bottom: none;
    }
  

  
    &.${optionUnstyledClasses.highlighted}.${optionUnstyledClasses.selected} {
      background-color: #F4F1E6;
    }
  
    &.${optionUnstyledClasses.disabled} {
      color: ${theme.palette.mode === "dark" ? grey[700] : grey[400]};
    }
  
    &:hover:not(.${optionUnstyledClasses.disabled}) {
      background-color: #F4F1E6;
    }
    `,
);

const StyledPopper = styled(PopperUnstyled)`
  z-index: 1;
  position: relative;
  width: 100%;
`;

export const CustomSelect = React.forwardRef(function CustomSelect(props, ref) {
  const slots = {
    root: StyledButton,
    listbox: StyledListbox,
    popper: StyledPopper,
    ...props.slots,
  };

  return (
    <SelectUnstyled
      className={props.className}
      {...props}
      ref={ref}
      slots={slots}
      onChange={(_, v) => props.onChange(v)}
    />
  );
});

export const CustomOptionRefresh = React.forwardRef(
  function CustomSelect(props, ref) {
    return (
      <OptionUnstyled
        slotProps={{
          root: {
            className:
              "w-100 px-5 py-6 text-base font-normal text-onmoGrey-medium bg-onmoPurple-50 border-b-[0.6px] border-t-[0.6px] last:rounded-b-lg flex justify-content-between cursor-pointer",
          },
        }}
        {...props}
        ref={ref}
      />
    );
  },
);

const Button = React.forwardRef(function Button(props, ref) {
  const { ownerState, ...other } = props;

  return (
    <button
      type="button"
      {...other}
      ref={ref}
      className={`w-full px-5 py-6 text-base font-normal text-onmoPrimaryBlack bg-onmoPurple-50 border-0 text-left cursor-pointer filter-none flex justify-between ${
        ownerState.open ? "rounded-t-lg" : "rounded-lg"
      }`}
    >
      {other.children}
      <span
        className={`w-6 h-6 flex justify-center text-onmoPrimaryBlack align-middle ${
          ownerState.open ? "rotate-180" : ""
        } transition-transform duration-300`}
      >
        <ChevronDown />
      </span>
    </button>
  );
});

export const CustomSelectRefresh = React.forwardRef(
  function CustomSelect(props, ref) {
    return (
      <SelectUnstyled
        {...props}
        slots={{
          root: Button,
        }}
        slotProps={{
          listbox: {
            className:
              "w-full text-onmoPrimaryBlack bg-onmoPurple-50 shadow-lg border-0",
          },
          popper: { className: "relative w-full" },
        }}
        renderValue={selected => {
          return (
            selected && (
              <span className="flex justify-between">{selected.label}</span>
            )
          );
        }}
        ref={ref}
      />
    );
  },
);

export const CustomSelectMultiLine = React.forwardRef(
  function CustomSelect(props, ref) {
    const slots = {
      root: StyledButtonMultiLine,
      listbox: StyledListbox,
      popper: StyledPopper,
      ...props.slots,
    };

    return (
      <SelectUnstyled
        {...props}
        ref={ref}
        slots={slots}
        onChange={(_, v) => props.onChange(v)}
      />
    );
  },
);

export const PostcodeSearch = ({
  passAddress,
  disabled,
  defaultValue = "",
}) => {
  const regex = new RegExp(/^[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}$/i);
  const [options, setOptions] = React.useState([]);
  const [value, setValue] = React.useState(defaultValue);
  const debouncedValue = useDebounce(value, 500);

  const search = async e => {
    if (regex.test(e)) {
      const body = {
        postcode: e,
      };
      try {
        const response = await fetch("/api/account/postcodecheck", {
          method: "POST",
          body: JSON.stringify(body),
        });
        const res = await response.json();
        if (response.status === 200) {
          if (res.addresses.length < 1) {
            var alt = {
              noResults: true,
              postcode: e,
              message:
                "No addresses found at this postcode - " +
                e.toLocaleUpperCase(),
            };
            setOptions([alt]);
          } else {
            const filteredResult = filterAddresses(res.addresses);
            setOptions(filteredResult);
          }
        } else {
          setOptions([e]);
        }
      } catch (err) {
        console.log(err);
        setOptions([e]);
      }
    }
  };

  useEffect(() => {
    const regex = new RegExp(/^[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}$/i);
    if (debouncedValue !== null && debouncedValue !== undefined) {
      if (debouncedValue === "") {
        setOptions([]);
      } else {
        if (regex.test(debouncedValue)) {
          search(debouncedValue);
          passAddress("noResult", debouncedValue);
        } else {
          passAddress("noResult", debouncedValue);
          setOptions([debouncedValue]);
        }
      }
    }
  }, [debouncedValue]);

  const displayAddress = addressData => {
    return displayFullAddress({ ...addressData, postcode: value });
  };

  return (
    <div>
      <label htmlFor="postCode" className={styles.inputTitle}>
        Postcode <span className="text-onmoRed">*</span>
      </label>
      <Autocomplete
        id="postCode"
        freeSolo
        disabled={disabled}
        sx={{
          display: "",
          "& input": {
            borderRadius: "5px",
            padding: "0 10px",
            fontSize: "16px",
            border: "1px solid #C4C4C4",
            height: "40px",
            width: "100%",
          },
        }}
        onKeyDown={event => {
          if (event.key === "Enter") {
            event.defaultMuiPrevented = true;
          }
        }}
        inputValue={value}
        loading
        loadingText="Please type your postcode to see the search results"
        onOpen={() => {
          const regex = new RegExp(/^[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}$/i);
          if (regex.test(value)) {
            search(value);
          }
        }}
        onInputChange={e => {
          if (!e) return;
          setValue(e.target.value);
        }}
        filterOptions={options => options}
        onChange={(e, v) => {
          if (v !== null) setValue(value.toLocaleUpperCase());
          if (options.length <= 1) {
            passAddress("manual", v);
          } else {
            passAddress(v, value);
          }
        }}
        options={options}
        forcePopupIcon
        isOptionEqualToValue={(option, value) => option === option}
        getOptionLabel={option =>
          option.line_1 === undefined
            ? value
            : option.line_1 + " " + option.locality
        }
        renderInput={params => (
          <div ref={params.InputProps.ref}>
            <input
              type="text"
              {...params.inputProps}
              className={styles.defaultInput}
              name="postal-code"
              autoComplete="off"
            />
          </div>
        )}
        renderOption={(props, option) => (
          <li {...props} className={styles.autoList} key={props.id}>
            {option.noResults
              ? option.message
              : option.line_1 === undefined
                ? value
                : displayAddress(option)}
          </li>
        )}
      />
    </div>
  );
};

export const CustomOtpInput = ({ otp, setOtp, otpError, old }) => {
  return (
    <div className="relative" data-testid="otp-form">
      <OtpInput
        data-testid="otp-verify-input"
        numInputs={4}
        inputStyle={old ? styles.oldOtpInput : styles.otpInput}
        isInputNum
        containerStyle="flex gap-5 justify-center"
        onChange={e => setOtp(e)}
        value={otp}
      />
      {otpError ? (
        <p className={styles.otpError}>Incorrect code. Please try again.</p>
      ) : (
        <p className={styles.otpError} />
      )}
    </div>
  );
};

export const DateOfBirthInput = ({
  onChange,
  disabled,
  dobError,
  ageError,
  value,
}) => {
  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor="dob"
        className={`${disabled ? "opacity-50" : ""} ${styles.inputTitle}`}
      >
        Date of birth
      </label>
      <NumberFormat
        id="dob"
        value={value}
        placeholder="DD/MM/YYYY"
        disabled={disabled}
        onValueChange={v => onChange(v)}
        format="##/##/####"
        className={styles.defaultInput}
      />
      {ageError ? (
        <p className="text-onmoRed">
          Sorry, you need to be over 18 to continue.
        </p>
      ) : null}
      {dobError ? (
        <p className="text-onmoRed">
          Hmm&#x2C; something doesn&apos;t look right here.
        </p>
      ) : null}
    </div>
  );
};

export const Input = ({
  onChange,
  label,
  type,
  disabled,
  value,
  error,
  onBlur,
  optional,
  errorMessage,
  hideError,
  onInputCheck,
}) => {
  const altError = hideError
    ? false
    : optional
      ? false
      : !!(value?.length < 1 || value === undefined);
  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={camelize(label)}
        className={`${disabled ? "opacity-50" : ""} ${styles.inputTitle}`}
      >
        {label}{" "}
        {optional ? (
          <span className={styles.optionalFlag}>&#40;optional&#41;</span>
        ) : null}
      </label>
      <input
        id={camelize(label)}
        className={styles.defaultInput}
        type={type}
        onChange={e => {
          if (onInputCheck) {
            return onInputCheck(e, onChange);
          }
          const re = /^[a-zA-Z0-9&@ .$%\-,():;`]+$/;
          if (re.test(e.target.value)) {
            onChange(e);
          }
          if (re.test(e.target.value) === false && e.target.value === "") {
            onChange(e);
          }
        }}
        disabled={disabled}
        value={value}
        onBlur={onBlur}
      />
      {error ? <p className={styles.inputErrorMessage}>{error}</p> : null}
      {altError ? (
        <p className={styles.inputErrorMessage}>
          {errorMessage ? errorMessage : "This field cannot be empty"}
        </p>
      ) : null}
    </div>
  );
};

export const FormikInput = ({
  label = "",
  type,
  disabled = false,
  placeholder = "",
  ...rest
}) => {
  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={camelize(label)}
        className={`${disabled ? "opacity-50" : ""} ${styles.inputTitle}`}
      >
        {label}{" "}
      </label>
      <input
        id={camelize(label)}
        className={styles.defaultInput}
        placeholder={placeholder}
        type={type}
        {...rest}
        disabled={disabled}
      />
    </div>
  );
};

export const BasicInput = ({
  onChange,
  label,
  type,
  disabled,
  value,
  optional,
  errorMessage,
  error,
  onBlur,
  name,
  hasError,
  onInputCheck,
}) => {
  const [displayError, setDisplayError] = React.useState(error);
  React.useEffect(() => {
    setDisplayError(error);
  }, [error, value]);
  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={camelize(label)}
        className={`${disabled ? "opacity-50" : ""} ${styles.inputTitle}`}
      >
        {label}{" "}
        {optional ? (
          <span className={styles.optionalFlag}>&#40;optional&#41;</span>
        ) : (
          <span className="text-onmoRed">*</span>
        )}
      </label>
      <input
        id={camelize(label)}
        name={name}
        className={styles.defaultInput}
        type={type}
        // Disable browser form autocomplete in MS Edge
        aria-autocomplete="none"
        onChange={e => {
          if (onInputCheck) {
            return onInputCheck(e, onChange, setDisplayError);
          }
          setDisplayError(false);
          const re = /^[a-zA-Z0-9&@ .$%\-,():;`]+$/;
          if (re.test(e.target.value)) {
            onChange(e);
          }
          if (re.test(e.target.value) === false && e.target.value === "") {
            onChange(e);
          }
        }}
        disabled={disabled}
        value={value}
        onBlur={
          onBlur
            ? onBlur
            : () => {
                if (value?.length < 1) {
                  setDisplayError(true);
                  hasError(true);
                } else {
                  setDisplayError(false);
                  hasError(false);
                }
              }
        }
      />
      {/* {error ? <p className={styles.inputErrorMessage}>{error}</p> : null} */}
      {displayError && !optional ? (
        <p className={styles.inputErrorMessage}>
          {errorMessage ? errorMessage : "This field cannot be empty"}
        </p>
      ) : null}
    </div>
  );
};

export const MoneyInput = ({ label, onChange, value, help, id }) => {
  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  const defaultValue = {
    floatValue: null,
  };
  return (
    <div className="flex flex-col gap-2.5">
      <label htmlFor={id} className={styles.inputTitle}>
        {label}{" "}
        {help ? (
          <ClickAwayListener onClickAway={handleTooltipClose}>
            <Tooltip
              PopperProps={{
                disablePortal: true,
              }}
              onClose={handleTooltipClose}
              open={open}
              placement="top"
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={help}
            >
              <IconButton
                style={{ padding: "0px", color: "black" }}
                disableRipple
                onClick={handleTooltipOpen}
              >
                <HelpOutlineIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </ClickAwayListener>
        ) : null}
      </label>
      <NumberFormat
        id={id}
        prefix="£"
        className={styles.defaultInput}
        value={value}
        allowEmptyFormatting
        thousandSeparator
        onValueChange={v => {
          if (v.floatValue === undefined) onChange(defaultValue);
          onChange(v);
        }}
        allowNegative={false}
        onBlur={() => {
          if (value === null) setError(true);
          else setError(false);
        }}
      />
      {error && (
        <p className={styles.inputErrorMessage}>
          This field cannot be empty - if you have none please indicate so with
          a &apos;0&apos;
        </p>
      )}
    </div>
  );
};

export const FormikCurrencyInput = ({
  label = "",
  name,
  type,
  disabled = false,
  placeholder = "",
  onChange,
  value,
  ...rest
}) => {
  return (
    <NumberFormat
      id={name}
      prefix="£"
      className={`${styles.defaultInput} `}
      value={value}
      allowEmptyFormatting
      thousandSeparator
      {...rest}
      onValueChange={v => {
        onChange(v);
      }}
      allowNegative={false}
    />
  );
};
export const LargeAccordion = styled(props => (
  <MuiAccordion disableGutters elevation={0} {...props} />
))(({ theme }) => ({
  borderBottom: "1px solid #C4C4C4",
  backgroundColor: "#FCF8F8",
  borderRadius: "0px !important",
  "&:before": {
    display: "none",
  },
  "& .Mui-disabled": {
    backgroundColor: "#FCF8F8 !important",
    opacity: "1.0",
    // color: 'rgba(0, 0, 0, 0.4)'
  },
}));

export const Accordion = styled(props => (
  <MuiAccordion disableGutters elevation={0} {...props} />
))(({ theme }) => ({
  borderBottom: "1px solid #C4C4C4",
  "&:before": {
    display: "none",
  },
}));

export const AccordionSummary = styled(props => (
  <MuiAccordionSummary
    expandIcon={
      props.lock && !props.expanded ? (
        <svg
          fill="#008675"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 50 50"
          width="23px"
          height="23px"
        >
          <path d="M 25 3 C 18.363281 3 13 8.363281 13 15 L 13 20 L 9 20 C 7.300781 20 6 21.300781 6 23 L 6 47 C 6 48.699219 7.300781 50 9 50 L 41 50 C 42.699219 50 44 48.699219 44 47 L 44 23 C 44 21.300781 42.699219 20 41 20 L 37 20 L 37 15 C 37 8.363281 31.636719 3 25 3 Z M 25 5 C 30.566406 5 35 9.433594 35 15 L 35 20 L 15 20 L 15 15 C 15 9.433594 19.433594 5 25 5 Z M 25 30 C 26.699219 30 28 31.300781 28 33 C 28 33.898438 27.601563 34.6875 27 35.1875 L 27 38 C 27 39.101563 26.101563 40 25 40 C 23.898438 40 23 39.101563 23 38 L 23 35.1875 C 22.398438 34.6875 22 33.898438 22 33 C 22 31.300781 23.300781 30 25 30 Z" />
        </svg>
      ) : props.tick && !props.expanded ? (
        <svg
          width="23"
          height="23"
          viewBox="0 0 23 23"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="11.5" cy="11.5" r="11.5" fill="#008675" />
          <path
            d="M6 12.8L9.42857 16L18 8"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg
          width="22"
          height="11"
          viewBox="0 0 22 11"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11 10.7391C10.3977 10.7402 9.80107 10.6225 9.24427 10.3929C8.68747 10.1632 8.18139 9.8261 7.75497 9.40075L0.2658 1.91158C0.0931881 1.73897 -0.00378418 1.50486 -0.00378418 1.26075C-0.00378418 1.01664 0.0931881 0.782528 0.2658 0.609916C0.438412 0.437304 0.672523 0.340332 0.916633 0.340332C1.16074 0.340332 1.39485 0.437304 1.56747 0.609916L9.05663 8.09908C9.57226 8.61407 10.2712 8.90333 11 8.90333C11.7287 8.90333 12.4277 8.61407 12.9433 8.09908L20.4325 0.609916C20.6051 0.437304 20.8392 0.340332 21.0833 0.340332C21.3274 0.340332 21.5615 0.437304 21.7341 0.609916C21.9067 0.782528 22.0037 1.01664 22.0037 1.26075C22.0037 1.50486 21.9067 1.73897 21.7341 1.91158L14.245 9.40075C13.8185 9.8261 13.3125 10.1632 12.7557 10.3929C12.1989 10.6225 11.6023 10.7402 11 10.7391Z"
            fill={getPrimaryColor()}
          />
        </svg>
      )
    }
    {...props}
    className={`${props.tick || props.locked ? "" : "accordion-dropdown"} ${
      props.className
    }`}
  />
))(({ theme }) => ({
  padding: "0",
  [theme.breakpoints.up("md")]: {
    padding: "0 10px",
  },
  margin: "0",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(180deg)",
    borderBottom: "none",
  },
}));

export const ContractAccordionSummary = styled(props => (
  <MuiAccordionSummary
    expandIcon={
      <svg
        width="22"
        height="11"
        viewBox="0 0 22 11"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11 10.7391C10.3977 10.7402 9.80107 10.6225 9.24427 10.3929C8.68747 10.1632 8.18139 9.8261 7.75497 9.40075L0.2658 1.91158C0.0931881 1.73897 -0.00378418 1.50486 -0.00378418 1.26075C-0.00378418 1.01664 0.0931881 0.782528 0.2658 0.609916C0.438412 0.437304 0.672523 0.340332 0.916633 0.340332C1.16074 0.340332 1.39485 0.437304 1.56747 0.609916L9.05663 8.09908C9.57226 8.61407 10.2712 8.90333 11 8.90333C11.7287 8.90333 12.4277 8.61407 12.9433 8.09908L20.4325 0.609916C20.6051 0.437304 20.8392 0.340332 21.0833 0.340332C21.3274 0.340332 21.5615 0.437304 21.7341 0.609916C21.9067 0.782528 22.0037 1.01664 22.0037 1.26075C22.0037 1.50486 21.9067 1.73897 21.7341 1.91158L14.245 9.40075C13.8185 9.8261 13.3125 10.1632 12.7557 10.3929C12.1989 10.6225 11.6023 10.7402 11 10.7391Z"
          fill={getPrimaryColor()}
        />
      </svg>
    }
    {...props}
    className={`${props.className} accordion-dropdown`}
  />
))(({ theme }) => ({
  lineHeight: "29px",
  fontSize: "18px",
  padding: "0",
  [theme.breakpoints.up("md")]: {
    padding: "0 10px",
  },
  margin: "0",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(180deg)",
    borderBottom: "none",
  },
  "& .Mui-expanded": {
    color: getPrimaryColor(),
    // borderBottom: '2px solid #FC4C02',
  },
  "& .MuiPaper-root": {
    backgroundColor: "red",
  },
}));

export const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: "0 10px",
}));

export default function CustomizedAccordions({
  acceptHandler,
  one,
  two,
  three,
  four,
  buttonHandler,
  children,
  expanded,
  setExpanded,
}) {
  const ref = React.useRef(null);

  const tick = (
    <svg
      className="inline ml-2"
      width="23"
      height="23"
      viewBox="0 0 23 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="11.5" cy="11.5" r="11.5" fill="#008675" />
      <path
        d="M6 12.8L9.42857 16L18 8"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const handleChange = panel => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
    buttonHandler(false, panel);
  };

  return (
    <div>
      <Accordion expanded={expanded === 1} onChange={handleChange(1)}>
        <ContractAccordionSummary
          aria-controls="panel1d-content"
          id="panel1d-header"
        >
          <p ref={ref} className="w-full break-words">
            1. Adequate explanations {one ? tick : null}
          </p>
        </ContractAccordionSummary>
        <AccordionDetails>
          <div className="flex flex-col gap-5 text-lg">
            {children}
            {!one && (
              <button
                onClick={() => {
                  setExpanded(false);
                  acceptHandler(1);
                }}
                className={styles.documentBoxButton}
              >
                Accept
              </button>
            )}
          </div>
        </AccordionDetails>
      </Accordion>
      <Accordion expanded={expanded === 2} onChange={handleChange(2)}>
        <ContractAccordionSummary
          aria-controls="panel2d-content"
          id="panel2d-header"
        >
          <p className="w-full break-words">
            2. Pre-contract credit information (PCCI) {two ? tick : null}
          </p>
        </ContractAccordionSummary>
        <div className="flex flex-col gap-5 text-lg">
          {children}
          {!two && (
            <button
              onClick={() => {
                setExpanded(false);
                acceptHandler(2);
              }}
              className={styles.documentBoxButton}
            >
              Accept
            </button>
          )}
        </div>
      </Accordion>
      <Accordion expanded={expanded === 3} onChange={handleChange(3)}>
        <ContractAccordionSummary
          aria-controls="panel3d-content"
          id="panel3d-header"
        >
          <p className="w-full break-words">
            3. Credit agreement {three ? tick : null}
          </p>
        </ContractAccordionSummary>
        <AccordionDetails>
          <div className="flex flex-col gap-5 text-lg">
            {children}
            {!three && (
              <button
                onClick={() => {
                  setExpanded(false);
                  acceptHandler(3);
                }}
                className={styles.documentBoxButton}
              >
                Accept
              </button>
            )}
          </div>
        </AccordionDetails>
      </Accordion>
      <Accordion expanded={expanded === 4} onChange={handleChange(4)}>
        <ContractAccordionSummary
          aria-controls="panel4d-content"
          id="panel4d-header"
        >
          <p className="w-full break-words">
            4. Card terms and conditions {four ? tick : null}
          </p>
        </ContractAccordionSummary>
        <AccordionDetails>
          <div className="flex flex-col gap-5 text-lg">
            {children}
            {!four && (
              <button
                onClick={() => {
                  setExpanded(false);
                  acceptHandler(4);
                }}
                className={styles.documentBoxButton}
              >
                Accept
              </button>
            )}
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
